import { query } from 'api/prismic';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { layoutQuery } from 'prismic/fragments/layoutQuery';
import { powerstationQuery } from 'prismic/queries/powerstationQuery';
import { Layout, Power_Station } from 'prismic-types';

import { BackButton } from 'components/back-button/BackButton';
import { Section } from 'components/section/Section';
import { PrismicMeta } from 'containers/meta/PrismicMeta';
import { PowerStationContainer } from 'containers/page/PowerStationContainer';
import { PowerStationSlices } from 'containers/slices/PowerStationSlices';

import { localeToPrismicLocale } from 'utils/i18n';
import {
  singleNavigationQuery,
  topNavigationQuery,
} from 'prismic/fragments/navigationQuery';

export type PageProps = {
  preview: boolean;
  page: Power_Station | null;
  layout: Layout | null;
};

export default function PageComponent(
  data: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const router = useRouter();

  const page = data.page ?? null;

  if (!page) {
    return null;
  }

  return (
    <>
      <PrismicMeta data={page} layout={data.layout} />
      <PowerStationContainer data={page} />

      <PowerStationSlices slices={page.slices ?? []} />
      <Section container>
        <BackButton
          onClick={() => {
            router.back();
          }}
        >
          Til baka
        </BackButton>
      </Section>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  preview = false,
  previewData,
  params,
  resolvedUrl,
  locale,
}) => {
  const lang = localeToPrismicLocale(locale);
  const uid = params?.uid as string;
  const variables = { uid, lang };
  const cacheKey = `${lang}-uid-${uid}`;

  // GraphQL hack for deep navigation
  const topNavData = await query(topNavigationQuery, { variables: { lang } });
  const topNav =
    topNavData?.allNavigations.edges
      ?.filter((edge) => edge && edge.node)
      .map((edge) => {
        return edge?.node._meta.uid;
      }) ?? [];

  const megamenuItems = async () => {
    return await Promise.all(
      topNav.map((uid) =>
        query(singleNavigationQuery, { variables: { lang, uid: uid! } })
      )
    );
  };

  const [navData, layoutData, pageData] = await Promise.all([
    megamenuItems(),
    query(layoutQuery, {
      variables: { lang },
      cacheKey: `layout-${lang}`,
    }),
    query(powerstationQuery, {
      previewData,
      variables,
      cacheKey: `powerstation-${cacheKey}`,
      breadcrumbs: [resolvedUrl],
    }),
  ]);

  const layout: Layout | null = layoutData?.layout ?? null;
  const page: Power_Station | null = pageData?.power_station ?? null;

  const navigation = navData.map((navDataItem) => navDataItem?.navigation);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      preview,
      navigation,
      page,
      layout,
    },
  };
};
